<template>
  <div>
    <nav-bar></nav-bar>
    <b-breadcrumb>
      <b-breadcrumb-item
        v-for="(item, index) in breadcrumbItems"
        :key="index"
        :href="item.href"
        :active="item.active"
      >
        <i v-if="item.icon" :class="item.icon"></i>
        <strong>{{ item.text }}</strong>
      </b-breadcrumb-item>
    </b-breadcrumb>
    <div>
      <b-card no-body>
        <b-card-header class="sticky-top" header-tag="nav" header-bg-variant="light">
          <b-nav card-header tabs fill>
            <b-nav-item
              v-for="lesson in lessons"
              :key="lesson.lessonId"
              :to="`/courses/${courseCode}/lesson/${lesson.lessonId}`"
              exact exact-active-class="active"
            >
              {{ lesson.lessonName }}
            </b-nav-item>
          </b-nav>
        </b-card-header>
        <div v-if="lessons.length > 1" class="d-flex align-items-center justify-content-center">
          <b-button variant="light">
            <template v-if="!bookmark.marked">
              <i
                class="fa-regular fa-bookmark fa-xl"
                style="color: #007bff"
                v-b-tooltip.hover
                :title="TOOLTIP.addBookmark"
                @click="addBookmark()"
              ></i>
            </template>
            <template v-else>
              <i
                class="fa-solid fa-bookmark fa-xl"
                style="color: #007bff"
                v-b-tooltip.hover
                :title="TOOLTIP.removeBookmark"
                @click="removeBookmark()"
              ></i>
              <b-link
                :to="`/courses/${courseCode}/lesson/${bookmark.lesson}`"
                class="mx-2"
                v-b-tooltip.hover
                :title="TOOLTIP.openBookmark"
              >
                Go to {{ lessonName }}
              </b-link>
            </template>
          </b-button>
        </div>
        <b-card-body>
          <keep-alive>
            <router-view :course="course" :key="$route.params.lesson"></router-view>
          </keep-alive>
        </b-card-body>
      </b-card>
    </div>
  </div>
</template>

<script>
import NavBar from "@/components/CourseNav.vue";
import { getCourseWithLessons, putBookmark } from "@/api/course";
import { mapState } from "vuex";
export default {
  name: "CourseView",
  components: {
    NavBar
  },
  data() {
    return {
      course: {
        /** 课程名，兼用于面包屑栏显示当前课程名 */
        name: '',
        /** 课程介绍，用于默认页面的课程介绍 */
        description: ''
      },
      /** 课导航 */
      lessons: [],
      /** 书签 */
      bookmark: {
        sub: '',
        courseCode: '',
        marked: false,
        lesson: 0,
        updateBy: ''
      },
      TOOLTIP: {
        addBookmark: process.env.VUE_APP_ADD_BOOKMARK_TOOLTIP,
        removeBookmark: process.env.VUE_APP_REMOVE_BOOKMARK_TOOLTIP,
        openBookmark: process.env.VUE_APP_OPEN_BOOKMARK_TOOLTIP
      },
      /** 课名，用于书签表示名 */
      lessonName: '',
      /** 打开课的提示信息 */
      courseToast: process.env.VUE_APP_COURSE_TOAST,
      hideDelay: parseInt(process.env.VUE_APP_TOAST_HIDE_DELAY),
      title: process.env.VUE_APP_TITLE
    }
  },
  mounted() {
    this.fetchCourseData();
  },
  computed: {
    ...mapState('auth', ['currentUser']),
    /** 取得课程码 */
    courseCode() {
      return this.$route.params.code;
    },
    /** 面包屑 */
    breadcrumbItems() {
      const items = [
        {
          text: process.env.VUE_APP_BREADCRUMB_HOME || 'Home',
          href: process.env.VUE_APP_HOME_URL || 'https://johnslanguages.com',
          icon: 'fa-solid fa-house' // 首页添加图标
        },
        {
          text: process.env.VUE_APP_BREADCRUMB_LANGUAGES || 'Language Studies',
          href: process.env.VUE_APP_LANGUAGES_URL || 'https://johnslanguages.com/en',
          icon: 'fa-regular fa-folder-open'
        },
        {
          text: process.env.VUE_APP_BREADCRUMB_COURSES || 'Language Courses',
          href: (process.env.VUE_APP_URL || '') + '/home',
          icon: 'fa-regular fa-folder-open'
        }
      ];
      // 只有课程名称存在时才添加
      if (this.course?.name) {
        items.push({
          text: this.course.name,
          active: true
        });
      } else {
        // 如果没有课程名称，确保最后一个项目是active
        items[items.length - 1].active = true;
      }
      return items;
    }
  },
  methods: {
    /** 查询课程及其课列表 */
    async fetchCourseData() {
      try {
        const res = await getCourseWithLessons(this.courseCode, this.currentUser.sub);
        /** 课程介绍，用于默认页面显示课程介绍 */
        this.course = {
          name: res.name,
          description: res.description
        };
        /** 代入课导航栏数值 */
        this.lessons = res.lessons;
        /** 代入书签数值 */
        this.bookmark = {
          ...this.bookmark,
          marked: res.memberCourse.marked,
          lesson: res.memberCourse.lesson
        };
        /** 当书签被设定时，显示书签以及所在课名 */
        if (this.bookmark.marked) {
          this.lessonName = this.getLessonName(this.bookmark.lesson);
        }
      } catch (error) {
        console.error('Error loading course:', error);
      }
    },
    /** 设定书签 */
    addBookmark() {
      const lessonId = this.$route.params.lesson;
      /** 当还没有进入课而设定书签，提示必须打开课 */
      if (!lessonId) {
        const message = this.courseToast;
        const variant = 'info';
        this.showToast(message, variant);
        return;
      }
      /** 设定书签值 */
      Object.assign(this.bookmark, {
        sub: this.currentUser.sub,
        courseCode: this.courseCode,
        marked: true,
        lesson: lessonId,
        updateBy: this.currentUser.username
      });
      /** 显示书签的课名 */
      this.lessonName = this.getLessonName(lessonId);
      /** 存入数据库 */
      putBookmark(this.courseCode, this.bookmark);
      const message = `Bookmark is set to "${this.lessonName}"`;
      const variant = 'info';
      this.showToast(message, variant);
    },
    /** 取消书签 */
    removeBookmark() {
      /** 设定书签值 */
      Object.assign(this.bookmark, {
        sub: this.currentUser.sub,
        courseCode: this.courseCode,
        marked: false,
        updateBy: this.currentUser.username
      });
      /** 存入数据库 */
      putBookmark(this.courseCode, this.bookmark);
      const message = `Bookmark is deleted`;
      const variant = 'info';
      this.showToast(message, variant);
    },
    /** 根据课主键，从课列表中取得课名 */
    getLessonName(lessonId) {
      const found = this.lessons.find(item => item.lessonId === parseInt(lessonId));
      return found ? found.lessonName : '';
    },
    showToast(message, variant) {
      this.$bvToast.toast(message, {
        title: this.title,
        autoHideDelay: this.hideDelay || 1000,
        solid: true,
        variant: variant,
        toaster: 'b-toaster-top-center'
      });
    }
  }
}
</script>

<style scoped>

</style>
